/** @format */

import {
  List as TabsList,
  Root as TabsRoot,
  Trigger as TabsTrigger,
} from "@radix-ui/react-tabs";
import * as RadixTooltip from "@radix-ui/react-tooltip";
import { type FC, useEffect, useRef, useState } from "react";
import { isDesktop } from "react-device-detect";
import { cn } from "../../../util/classNameConstructor";
import Icon from "../../core/icon/Icon";
import type { TabsProps } from "./Tabs.types";

const Tabs: FC<TabsProps<string | number>> = ({
  tabs,
  active,
  setActive,
  hideTabText,
  classNames,
  type = "underline",
  orientation = "horizontal",
  withBorder,
}) => {
  const [width, setWidth] = useState(0);
  const [offset, setOffset] = useState(0);
  const tabRef = useRef<HTMLButtonElement>(null);

  const handleClick = (tab?: number | string, index?: number) => {
    setActive?.(tab || (index !== undefined ? index + 1 : 1));
  };

  const updateUnderline = () => {
    if (tabRef.current) {
      if (orientation === "horizontal") {
        setWidth(tabRef.current.offsetWidth);
        setOffset(tabRef.current.offsetLeft);
      } else {
        setWidth(tabRef.current.offsetHeight);
        setOffset(tabRef.current.offsetTop);
      }
    }
  };

  useEffect(() => {
    updateUnderline();
  }, [active, orientation, tabs]);

  useEffect(() => {
    window.addEventListener("resize", updateUnderline);
    return () => {
      window.removeEventListener("resize", updateUnderline);
    };
  }, [active, orientation, tabs]);

  useEffect(() => {
    if (tabs.length === 1 && setActive) {
      setActive(tabs[0].tab || 1);
    }
  }, [tabs, setActive]);

  return (
    <div className={cn("relative h-10 w-full max-w-max", classNames?.wrapper)}>
      <TabsRoot
        orientation={orientation}
        value={String(active)}
        className={cn(
          "h-full w-full  rounded-xl",
          type === "filled" && "bg-surface-subdued",
        )}
      >
        <TabsList
          className={cn(
            "relative flex h-full items-center",
            type === "filled" &&
              "w-full rounded-xl border-2 border-border-subdued bg-grey-300 p-0.5",
            type !== "filled" && "w-auto",
            orientation === "vertical" ? "flex-col" : "flex-row",
          )}
        >
          {(type === "underline" || type === "filled") && (
            <div
              className={cn(
                "absolute z-0 transition-all duration-200 left-0 ",
                orientation === "horizontal"
                  ? cn(
                      type === "underline" ? "bottom-0 h-[4px]" : "h-full",
                      type === "filled"
                        ? "bg-white rounded-xl"
                        : "bg-action-primary-default rounded",
                      "transform",
                    )
                  : cn(
                      "left-0.5 w-1",
                      type === "filled"
                        ? "bg-white rounded-xl"
                        : "bg-action-primary-default rounded",
                    ),
              )}
              style={{
                width: orientation === "horizontal" ? width : undefined,
                height: orientation === "vertical" ? width : undefined,
                transform: `translate${orientation === "horizontal" ? "X" : "Y"}(${offset}px)`,
              }}
            />
          )}
          {tabs.map(({ id, text, tab, icon, dataCy, disabled }, index) => {
            // Show tooltip for non-active tabs when text is hidden (icon-only) and on desktop
            const isActive = active === tab || active === index + 1;
            const isTextHidden = tabs.length > 2 && hideTabText && !isActive;
            const shouldShowTooltip = !isActive && isTextHidden && isDesktop;

            if (shouldShowTooltip) {
              return (
                <RadixTooltip.Provider
                  key={id || text || tab}
                  delayDuration={500}
                >
                  <RadixTooltip.Root>
                    <RadixTooltip.Trigger asChild>
                      <TabsTrigger
                        id={id}
                        value={String(tab || index + 1)}
                        onClick={() => handleClick(tab, index)}
                        disabled={disabled}
                        ref={
                          active === tab || active === index + 1 ? tabRef : null
                        }
                        data-cy={dataCy}
                        className={cn(
                          "focusable flex h-full items-center justify-center gap-1.5 whitespace-nowrap rounded-lg px-2.5 -mx-0.5",
                          type === "filled" && "w-full",
                          orientation === "vertical" && "flex-col",
                          disabled
                            ? "cursor-not-allowed opacity-20"
                            : "cursor-pointer",
                          active === tab || active === index + 1
                            ? "opacity-100"
                            : "opacity-50 hover:opacity-80",
                          type === "filled" && "z-[1]",
                        )}
                      >
                        {icon && <Icon name={icon} />}
                        <span
                          className={cn(
                            "body-md-bold leading-none",
                            active === tab || active === index + 1
                              ? classNames?.activeTab || classNames?.tab
                              : classNames?.tab,
                          )}
                        >
                          {tabs.length <= 2 || !hideTabText
                            ? text
                            : active === tab || active === index + 1
                              ? text
                              : null}
                        </span>
                      </TabsTrigger>
                    </RadixTooltip.Trigger>
                    <RadixTooltip.Portal>
                      <RadixTooltip.Content
                        className="bg-palette-neutral-1000 text-text-on-primary-depressed body-md rounded-xl px-3 py-2 shadow-lg z-[999999999999]"
                        sideOffset={5}
                      >
                        {text}
                        <RadixTooltip.Arrow className="fill-palette-neutral-1000" />
                      </RadixTooltip.Content>
                    </RadixTooltip.Portal>
                  </RadixTooltip.Root>
                </RadixTooltip.Provider>
              );
            }

            return (
              <TabsTrigger
                key={id || text || tab}
                id={id}
                value={String(tab || index + 1)}
                onClick={() => handleClick(tab, index)}
                disabled={disabled}
                ref={active === tab || active === index + 1 ? tabRef : null}
                data-cy={dataCy}
                className={cn(
                  "focusable flex h-full items-center justify-center gap-1.5 whitespace-nowrap rounded-lg px-2.5 -mx-0.5",
                  type === "filled" && "w-full",
                  orientation === "vertical" && "flex-col",
                  disabled ? "cursor-not-allowed opacity-20" : "cursor-pointer",
                  active === tab || active === index + 1
                    ? "opacity-100"
                    : "opacity-50 hover:opacity-80",
                  type === "filled" && "z-[1]",
                )}
              >
                {icon && <Icon name={icon} />}
                <span
                  className={cn(
                    "body-md-bold leading-none",
                    active === tab || active === index + 1
                      ? classNames?.activeTab || classNames?.tab
                      : classNames?.tab,
                  )}
                >
                  {tabs.length <= 2 || !hideTabText
                    ? text
                    : active === tab || active === index + 1
                      ? text
                      : null}
                </span>
              </TabsTrigger>
            );
          })}
        </TabsList>
      </TabsRoot>
      {withBorder && (
        <div className="bg-border-subdued -mt-[1px] h-[1px] w-full rounded-sm" />
      )}
    </div>
  );
};

export default Tabs;
